import { DraftState } from "./draftState";

// Stable signature for dirty comparison: serialise the generic draft state (the
// host maps this into its own persistence payload) so reverting an edit returns
// to "not dirty". sortOrder is always included so a pure reorder (drag-and-drop
// or any sortOrder-affecting action) is detected as a dirty change. Numeric
// code / template identity are the host's off-tree concern; for templates they
// are fully determined by codeText, so leaving them out here does not miss an
// edit. The 1099 box is user-editable, so it is part of the signature.
export const serializeDraftState = (state: DraftState): string =>
    JSON.stringify({
        accounts: state.accounts.map((account) => ({
            id: account.id,
            codeText: account.codeText,
            name: account.name,
            type: account.type,
            isDisabled: account.isDisabled,
            isManuallyCreated: account.isManuallyCreated,
            role: account.role ?? null,
            form1099Box: account.form1099Box ?? null,
            parentGroupId: account.parentGroupId,
            sortOrder: account.sortOrder,
        })),
        groups: state.groups.map((group) => ({
            id: group.id,
            name: group.name,
            type: group.type,
            parentGroupId: group.parentGroupId,
            sortOrder: group.sortOrder,
        })),
    });

export const isStateDirty = ({
    state,
    baselineSignature,
}: {
    state: DraftState;
    baselineSignature: string;
}): boolean => serializeDraftState(state) !== baselineSignature;

// Decides which draft state to keep once an async save resolves. Cells stay
// editable while the save is in flight, so the user may have changed the draft
// between dispatching the request and its response. If the draft is unchanged
// since the save started (its signature still equals `snapshotSignature`), it
// is safe to adopt the freshly-persisted server state (the host maps the server
// response into a DraftState first). Otherwise the user edited mid-flight, so
// the current draft is kept verbatim — overwriting it would silently discard
// those edits. Kept edits stay dirty and full-replace on the next save.
export const selectDraftStateAfterSave = ({
    currentState,
    serverState,
    snapshotSignature,
}: {
    currentState: DraftState;
    serverState: DraftState;
    snapshotSignature: string;
}): DraftState =>
    serializeDraftState(currentState) === snapshotSignature
        ? serverState
        : currentState;
